import * as analytic from '@anm/analytic';
import { Landing, LandingMetaProps, LandingTrackProps, Webinar } from '@anm/api/modules/hosting';
import CopyLink from '@anm/components/CopyLink';
import CallToActionForm from '@anm/components/form/CallToActionForm';
import LandingMetaForm from '@anm/components/form/LandingMetaForm';
import Switch from '@anm/components/form/Switch';
import TrackingForm from '@anm/components/form/TrackingForm';
import SectionTitle from '@anm/components/video-landings/SectionTitle';
import isMobileOrIPad from '@anm/helpers/is/isMobileOrIPad';
import noop from '@anm/helpers/noop';
import escapeName from '@anm/helpers/string/escapeName';
import toDashCase from '@anm/helpers/string/toDashCase';
import { useCallback, ChangeEvent, FC } from 'react';
import { useSelector } from 'react-redux';

import { landingsSelectors } from '../../../../../store/modules/landings';
import { TitleWithToggler } from '../../Wrapper';
import useTogglers from '../../hooks/useTogglers';

import DesignSelection from './DesignSelection';
import {
  CallToActionWrapper,
  ChatWrapper,
  FormWrapper,
  LandingContentWrapper,
  LoaderWrapper,
  ShareBlock,
  ShareBlockDescription,
  ShareWrapper,
  TrackingWrapper
} from './Wrapper';
import { canUpdateParams } from './helpers';
import {
  useCallToActionForm,
  useDefaultButtonText,
  useLandingTabActions,
  useLandingTabInitialValues,
  useShareLink,
  useToggleChat
} from './hooks';
import { CustomizationTabIndexes } from '../../../../../pages/video';
import CustomizationFormCheckbox from '../../../CustomizationForm/CustomizationFormCheckbox';
import useIsWebinar from '../../../../../hooks/useIsWebinar';

export type LandingTabProps = {
  hostId: string;
};

type LandingContentProps = LandingTabProps & {
  landing: Landing;
};

const LandingContent: FC<LandingContentProps> = ({ landing, hostId }) => {
  const { public: isLandingPublic = true, parameters } = landing;
  const [CTAFormValues, metaFormValues, trackingFormValues] = useLandingTabInitialValues(parameters);
  const slug = toDashCase(escapeName()(parameters?.meta?.heading || ''));
  const [updateLandingParams] = useLandingTabActions(landing);
  const isCTADisabled = typeof parameters?.cta?.disabled !== 'undefined' ? parameters?.cta?.disabled : true;
  useDefaultButtonText({ landing, isCTADisabled, updateLandingParams });

  const isWebinar = useIsWebinar();
  const [shareLink] = useShareLink({
    slug,
    hostId,
    isWebinar
  });

  const sectionTitle = isWebinar ? 'Webinar page' : 'Video landing page';
  const pageType = isWebinar ? 'webinar' : 'landing';

  const shareDescription = isLandingPublic
    ? `Only people with this link can see your ${pageType} page. This video is
  not listed anywhere`
    : `This ${pageType} page is only visible to you.`;

  const onMetaFormChange = useCallback(
    (params: LandingMetaProps) => {
      const canUpdate = canUpdateParams('meta', params, parameters);

      canUpdate &&
        updateLandingParams({
          params: params,
          key: 'meta'
        });
    },
    [parameters]
  );

  const onTrackFieldChange = useCallback(
    (params: LandingTrackProps) => {
      const canUpdate = canUpdateParams('track', params, parameters);

      canUpdate &&
        updateLandingParams({
          params,
          key: 'track'
        });
    },
    [parameters]
  );

  const onCTAStatusChange = useCallback(
    ({ target: { checked } }: ChangeEvent<HTMLInputElement>) => {
      analytic.trackLandingCTAClick(shareLink);

      updateLandingParams({
        key: 'cta',
        params: {
          ...parameters?.cta,
          disabled: !checked
        }
      });
    },
    [parameters, isCTADisabled]
  );

  const [togglers] = useTogglers<CustomizationTabIndexes>({ hostId });

  const { landing: landingToggler } = togglers;

  const [onCTAFormChange] = useCallToActionForm();

  const [isChat, onToggleChat] = useToggleChat(landing as Webinar);

  return (
    <LandingContentWrapper>
      <TitleWithToggler>
        <SectionTitle title={sectionTitle} />
        {isMobileOrIPad() && !isWebinar && <Switch checked={landingToggler.status} onChange={landingToggler.handler} />}
      </TitleWithToggler>
      <ShareWrapper>
        <ShareBlock>
          <CopyLink
            link={shareLink}
            buttonSize="medium"
            buttonText="Copy link"
            inputVariant="disabled_gray"
            buttonVariant="white-blue-border-share"
            isFieldClickable={true}
          />
        </ShareBlock>
        <ShareBlockDescription>{shareDescription}</ShareBlockDescription>
      </ShareWrapper>
      {!isWebinar && (
        <>
          <FormWrapper>
            <LandingMetaForm onSubmit={noop} initialValues={metaFormValues} onChange={onMetaFormChange} />
          </FormWrapper>
          <TitleWithToggler>
            <SectionTitle title="Call-to-action button" level={2} />
            <Switch checked={!isCTADisabled} onChange={onCTAStatusChange} />
          </TitleWithToggler>
          <CallToActionWrapper>
            <CallToActionForm onSubmit={noop} onChange={onCTAFormChange} initialValues={CTAFormValues} />
          </CallToActionWrapper>
        </>
      )}
      {isWebinar && (
        <ChatWrapper>
          <CustomizationFormCheckbox
            id="chat"
            label="Live chat"
            onChange={onToggleChat}
            key={`webinar-${isChat.toString()}`}
            isDefaultChecked={isChat}
            controlValue={isChat}
          />
        </ChatWrapper>
      )}
      <DesignSelection {...{ landing, isWebinar }} />
      {!isWebinar && (
        <TrackingWrapper>
          <SectionTitle title="Tracking" level={2} />
          <TrackingForm onSubmit={noop} initialValues={trackingFormValues} onChange={onTrackFieldChange} />
        </TrackingWrapper>
      )}
    </LandingContentWrapper>
  );
};

const LandingTab: FC<LandingTabProps> = props => {
  const landing = useSelector(landingsSelectors.selectLanding);

  if (!landing) return <LoaderWrapper size="large" color="blue" />;

  return <LandingContent {...{ ...props, landing }} />;
};

export default LandingTab;
